import { useActor, useSelector } from "@xstate/react";
import {
  EditTicketEventApiRequirements,
  EditTicketEventMachine,
  TicketEventForm,
} from "./EditTicketEventMachine";
import {
  getPlatforms,
  IonContent,
  IonIcon,
  IonModal,
  IonPage,
  IonSpinner,
  IonToggle,
  useIonToast,
} from "@ionic/react";
import {
  FloatingBackButton,
  PageContainer,
  DateTimePeriodInput,
  DatePeriodInput,
  Spinner,
} from "@greeter/matter";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  HiddenUploadInput,
  InputController,
} from "@greeter-venue/components/UploadInput";
import { PriceInput, TextInput } from "@greeter/matter-v2";
import { classNames, isType } from "@greeter/util";
import { imageOutline } from "ionicons/icons";
import "@mdxeditor/editor/style.css";
import {
  BoldItalicUnderlineToggles,
  MDXEditor,
  UndoRedo,
  headingsPlugin,
  listsPlugin,
  BlockTypeSelect,
  ListsToggle,
  markdownShortcutPlugin,
  toolbarPlugin,
  MDXEditorMethods,
} from "@mdxeditor/editor";
import { DatePeriod, DatePeriodSchema } from "@greeter/date";

import { AnimatePresence, motion } from "motion/react";
import { dropInOutFromBottom } from "@greeter/motion";
import { assert } from "@greeter/assert";
import { useIdle } from "@greeter/hooks";
import {
  IconAlertCircle,
  IconCheck,
  IconCopy,
  IconInfoCircle,
  IconPlus,
} from "@tabler/icons-react";
import { Log } from "@greeter/log";
import { useFetchActorLoading } from "@greeter/xstate-util";
import { Capacitor } from "@capacitor/core";
import { Share } from "@capacitor/share";
import { useHistory } from "react-router-dom";
import { Routes } from "@greeter-venue/utility/Routes";
import { Bundle, ImageAsset } from "@greeter/core";
import { indexBy } from "@greeter/map";
import { isTruthy } from "@greeter/array";
import { formatMoney } from "@greeter/formatters";

import css from "./EditTicketEventPage.module.scss";

async function copyToClipboard(opts: {
  title: string;
  url: string;
  onCopied?: (copyType: "share" | "copy") => void;
  onError?: () => void;
}) {
  // We check for it to be desktop since the share functionality
  // on Safari is absolutely horrendous.
  // and so if we detect it, we should simply copy the text to
  // clipboard instead.
  const isDesktop = new Set(getPlatforms()).has("desktop");

  if (Capacitor.isNativePlatform() && (await Share.canShare())) {
    const r = await Share.share({ title: opts.title, url: opts.url });
    Log.debug("Native share", { result: r });
    opts.onCopied?.("share");
  } else if (navigator.share && !isDesktop) {
    await navigator.share(opts);
    opts.onCopied?.("share");
  } else if (navigator.clipboard) {
    navigator.clipboard.writeText(opts.url);
    opts.onCopied?.("copy");
  } else {
    opts.onError?.();
  }
}

function EventUrlCopyThing(props: { title: string; slug: string }) {
  const [toast] = useIonToast();
  return (
    <motion.div>
      <h2 style={{ textAlign: "left", color: "var(--gm-color-dark-primary)" }}>
        Her er dit link
      </h2>
      <p
        style={{ textAlign: "left", color: "var(--gm-color-dark-primary)" }}
        onClick={() =>
          copyToClipboard({
            title: `${props.title}`,
            url: `https://app.greeter.dk/events/${props.slug}`,
            onError() {
              toast("Kunne ikke kopiere linket", 1000);
            },
            onCopied(copyType) {
              if (copyType === "copy") {
                toast("📋 Event link kopieret", 1000);
              }
            },
          })
        }
      >
        https://app.greeter.dk/events/{props.slug}
      </p>
    </motion.div>
  );
}

function Section({
  className,
  children,
  ...props
}: React.ComponentProps<"section">) {
  return (
    <section {...classNames(className, css.Section)} {...props}>
      <Section.Marker />
      {children}
    </section>
  );
}
Section.Marker = function () {
  return <div className={css.SectionMarker} />;
};
Section.Title = function ({ className, ...props }: React.ComponentProps<"h1">) {
  return <h1 {...classNames(className, css.SectionTitle)} {...props} />;
};

Section.SubTitle = function ({
  className,
  ...props
}: React.ComponentProps<"h2">) {
  return <h2 {...classNames(className, css.SectionSubTitle)} {...props} />;
};

function Toggle({ className, ...props }: React.ComponentProps<"div">) {
  return <div {...classNames(className, css.Toggle)} {...props} />;
}

Toggle.Button = function ToggleButton(props: {
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <IonToggle
      mode="ios"
      checked={props.checked}
      onIonChange={(e) => {
        props.onChange(e.detail.checked);
      }}
    />
  );
};
Toggle.Text = function ToggleText({
  className,
  ...props
}: React.ComponentProps<"p">) {
  return <p {...classNames(className, css.ToggleText)} {...props} />;
};

function ErrorHint(props: { error?: string | false }) {
  return (
    props.error && (
      <motion.small className={css.ErrorHint} {...dropInOutFromBottom}>
        <IconAlertCircle className={css.ErrorHintIcon} />
        {props.error}
      </motion.small>
    )
  );
}

function Button(props: {
  invalid?: boolean;
  onClick?: () => void;
  children?: React.ReactNode;
  className?: string;
  variant?: "outlined" | "default";
}) {
  return (
    <button
      {...classNames(
        props.className,
        css.Button,
        (props.variant ?? "default") === "default"
          ? css.ButtonDefault
          : css.ButtonOutlined,
        props.invalid && css.ButtonDisabled
      )}
      onClick={props.onClick}
    >
      {props.children}
    </button>
  );
}

function Css() {}

Css.url = function (s: string) {
  return `url(${s})`;
};

Button.IconCopy = function ButtonIcon(props: any) {
  return <IconCopy className={css.ButtonIcon} />;
};

Button.Text = function ButtonText({
  style,
  ...props
}: React.ComponentProps<"span">) {
  return <span className={css.ButtonText} {...props} />;
};

function BundleCard(props: {
  bundle: Bundle;
  isSelectable?: boolean;
  isSelected?: boolean;
  onClick?: () => void;
}) {
  return (
    <div
      style={
        {
          "--background-image-url": Css.url(
            ImageAsset.findUriWithSizeOrDefault(
              props.bundle.coverAsset,
              "16x9-w512"
            )
          ),
        } as any
      }
      {...classNames(css.BundleCard, props.isSelected && css.Selected)}
      onClick={props.onClick}
    >
      {props.isSelectable && (
        <div
          {...classNames(css.SelectedCheck, props.isSelected && css.Selected)}
        >
          {props.isSelected && <IconCheck className={css.IconCheck} />}
        </div>
      )}
      <div className={css.BundleCardContent}>
        <span className={css.BundleCardTitle}>{props.bundle.title}</span>
        <span className={css.BundleCardPrice}>
          {formatMoney(props.bundle.price)} kr.
        </span>
      </div>
    </div>
  );
}

function EmptyBundleCard(props: { onClick?: () => void }) {
  return (
    <div className={css.EmptyBundleCard} onClick={props.onClick}>
      <div className={css.EmptyBundleCardContent}>
        <IconPlus style={{ width: 64, height: 64 }} />
        Vælg bordpakke
      </div>
    </div>
  );
}

export type EditTicketEventPageProps = {
  api: EditTicketEventApiRequirements;
  id?: string;
};
export function EditTicketEventPage(props: EditTicketEventPageProps) {
  const [snap, send, m] = useActor(EditTicketEventMachine, {
    input: props,
  });
  const fileCtrl = useRef<InputController>(null);

  const api = useSelector(m, (s) => s.context.api);

  const fetchBundlesLoading = useFetchActorLoading(api.fetchBundlesRef);
  const bundles = useSelector(api.fetchBundlesRef, (s) => s.context.data);
  const indexedBundles = useMemo(
    () => indexBy(bundles ?? [], (b) => b.id),
    [bundles]
  );

  const fetchTicketEventLoading = useFetchActorLoading(api.fetchTicketEventRef);
  const fetchVenueLoading = useFetchActorLoading(api.fetchVenueRef);
  const uploading = useFetchActorLoading(api.uploadAssetRef);
  const saveTicketEventLoading = useFetchActorLoading(api.saveTicketEventRef);

  useEffect(() => {
    Log.debug("[EditTicketEventPage] fetchTicketEventLoading", {
      fetchTicketEventLoading,
    });
  }, [fetchTicketEventLoading]);

  const form = useSelector(m, (s) => s.context.form);
  const formErrors = useSelector(m, (s) => s.context.formErrors);
  const imageAsset = form.coverAssets[0];
  const touched = useSelector(m, (s) => s.context.touched);

  const indexedSelectedBundles = indexBy(
    form.bundles.map((id) => indexedBundles.get(id)).filter(isTruthy),
    (b) => b.id
  );

  const contentRef = useRef<HTMLIonContentElement>(null);
  const pageRef = useRef<HTMLDivElement>(null);
  const markdownRef = useRef<MDXEditorMethods>(null);

  useEffect(
    () =>
      api.saveTicketEventRef.on("fetched", (d) =>
        markdownRef.current?.setMarkdown(d.data.description)
      ).unsubscribe,
    [api]
  );
  useEffect(
    () =>
      api.fetchTicketEventRef.on("fetched", (d) =>
        markdownRef.current?.setMarkdown(d.data.description)
      ).unsubscribe,
    [api]
  );

  useEffect(() => {
    Log.debug("[EditTicketEventPage]", { form });
  }, [form]);

  const [tabBarHeight, setTabBarHeight] = useState(0);
  useEffect(() => {
    setTimeout(
      () =>
        setTabBarHeight(
          document.querySelector("ion-tab-bar")?.getBoundingClientRect()
            .height ?? 0
        ),
      1_500
    );
  }, []);

  const idle = useIdle(contentRef.current, { timeout: 1000 });
  useEffect(() => {
    if (idle) {
      send({ type: "validate" });
    }
  }, [idle]);

  function hasError(f: keyof TicketEventForm) {
    return touched.has(f) && !!formErrors?.properties?.[f]?.errors?.[0];
  }
  function getError(f: keyof TicketEventForm) {
    return (
      (hasError(f) && formErrors?.properties?.[f]?.errors?.[0]) ?? undefined
    );
  }
  function hasErrorThenOutline(f: keyof TicketEventForm) {
    return hasError(f) ? "error-outline" : "";
  }

  useEffect(() => {
    if (!pageRef.current) return;
    for (const inputElement of pageRef.current.querySelectorAll("input")) {
      inputElement.addEventListener("blur", () => send({ type: "validate" }));
    }
  }, [pageRef]);

  const [exitModalOpen, setExitModalOpen] = useState(false);

  useEffect(
    () =>
      api.saveTicketEventRef.on("fetched", () => setExitModalOpen(true))
        .unsubscribe,
    [api]
  );

  const [toast] = useIonToast();

  const history = useHistory();

  const [bundlePickerModalOpen, setBundlePickerModalOpen] = useState(false);

  return (
    <IonPage
      style={{
        // NOTE: This is necessary for MDXEditor not to break everything when using the block type plugin
        height: `calc(100dvh - ${tabBarHeight}px)`,
      }}
    >
      <FloatingBackButton />
      <IonContent ref={contentRef}>
        <PageContainer>
          <div ref={pageRef} className={css.EditTicketEventPage}>
            <div
              id="cover"
              onClick={() => {
                if (!fileCtrl.current) return;
                fileCtrl.current.show();
              }}
              {...classNames(
                css.CoverImage,
                hasErrorThenOutline("coverAssets")
              )}
            >
              <HiddenUploadInput
                onFile={(f) => {
                  if (f) {
                    send({ type: "upload", data: [{ data: f }] });
                  }
                }}
                onController={(ctrl) => {
                  fileCtrl.current = ctrl;
                }}
              />
              {form?.coverAssets[0]?.uri && (
                <img className={css.Image} src={imageAsset.uri} />
              )}
              <div className={css.ImageText}>
                {uploading ? (
                  <IonSpinner name="dots" className={css.ImageIcon} />
                ) : (
                  <IonIcon className={css.ImageIcon} icon={imageOutline} />
                )}
                <h4 style={{ margin: 0, textAlign: "center" }}>
                  Tryk for at uploade et billede
                </h4>
              </div>
            </div>
            <Section>
              <Section.Title>Grundlæggende informationer</Section.Title>
              <Section.SubTitle>Titel</Section.SubTitle>
              <TextInput
                className={hasErrorThenOutline("title")}
                value={form.title}
                onChange={(ev) =>
                  send({ type: "set.form.title", data: ev.currentTarget.value })
                }
                onBlur={() => send({ type: "touch", data: "title" })}
              />
              <ErrorHint error={getError("title")} />

              <Section.SubTitle>Dato og tidspunkt</Section.SubTitle>
              {isType(form.eventPeriod, "once") && (
                <DateTimePeriodInput
                  className={hasErrorThenOutline("eventPeriod")}
                  value={form.eventPeriod}
                  onValueChange={(v) => {
                    Log.info("[DateTimePeriodInput][onValueChange]", { v });
                    send({
                      type: "set.form.eventPeriod",
                      data: { type: "once", ...v },
                    });
                  }}
                  onBlur={() => send({ type: "touch", data: "eventPeriod" })}
                />
              )}
              <ErrorHint error={getError("eventPeriod")} />

              <Section.SubTitle>Address</Section.SubTitle>
              <div style={{ position: "relative" }}>
                <TextInput
                  className={hasErrorThenOutline("address")}
                  value={form.address}
                  style={{
                    paddingRight: 80,
                    opacity: fetchVenueLoading ? 0.5 : 1,
                  }}
                  onChange={(ev) =>
                    send({
                      type: "set.form.address",
                      data: ev.currentTarget.value,
                    })
                  }
                  onBlur={() => send({ type: "touch", data: "address" })}
                />
                {/* TODO: Make this more readable */}
                <motion.div
                  style={{
                    position: "absolute",
                    top: 0,
                    right: 10,
                    height: "100%",
                    display: "grid",
                    placeItems: "center",
                  }}
                >
                  <motion.button
                    style={{
                      borderRadius: 5,
                      display: "flex",
                      alignItems: "center",
                      gap: 3,
                      paddingLeft: 5,
                      paddingRight: 5,
                    }}
                    onClick={() => send({ type: "reset.address" })}
                  >
                    {fetchVenueLoading ? (
                      <IonSpinner name="dots" color="black" />
                    ) : (
                      <IconInfoCircle style={{ width: 15 }} />
                    )}
                    Reset
                  </motion.button>
                </motion.div>
              </div>

              <Section.SubTitle>Beskrivelse</Section.SubTitle>
              <MDXEditor
                ref={markdownRef}
                {...classNames(
                  css.MarkdownEditor,
                  hasErrorThenOutline("description")
                )}
                markdown={form.description}
                onChange={(markdown) =>
                  send({ type: "set.form.description", data: markdown })
                }
                overlayContainer={pageRef.current}
                plugins={[
                  headingsPlugin(),
                  listsPlugin(),
                  markdownShortcutPlugin(),
                  toolbarPlugin({
                    toolbarContents: () => (
                      <>
                        <UndoRedo />
                        <BlockTypeSelect />
                        <ListsToggle />
                        <BoldItalicUnderlineToggles />
                      </>
                    ),
                  }),
                ]}
                onBlur={() => send({ type: "touch", data: "description" })}
              />
              <ErrorHint error={getError("description")} />

              {/* TODO: Readd in the future */}
              {/* <Section.SubTitle>Offentlig</Section.SubTitle> */}
              {/* <Toggle> */}
              {/*   <Toggle.Text>Er eventet offentligt?</Toggle.Text> */}
              {/*   <Toggle.Button */}
              {/*     checked={form.publishingStatus === "published"} */}
              {/*     onChange={(c) => */}
              {/*       send({ */}
              {/*         type: "set.form.publishingStatus", */}
              {/*         data: c ? "published" : "draft", */}
              {/*       }) */}
              {/*     } */}
              {/*   /> */}
              {/* </Toggle> */}
            </Section>
            <Section>
              <Section.Title>Billetter</Section.Title>

              <Section.SubTitle>Pris</Section.SubTitle>
              {isType(form.tickets, "paid-single") &&
                !fetchTicketEventLoading && (
                  <PriceInput
                    value={form.tickets.price}
                    className={hasErrorThenOutline("tickets")}
                    onChange={(p) => {
                      assert(isType(form.tickets, "paid-single"));
                      send({
                        type: "set.form.tickets",
                        data: {
                          ...form.tickets,
                          price: p ?? 0,
                        },
                      });
                    }}
                    onBlur={() => send({ type: "touch", data: "tickets" })}
                  />
                )}

              <ErrorHint error={getError("tickets")} />

              {/* TODO: Add later! Functions, but we need the backend to properly support it */}
              {/* <Section.SubTitle>Antal</Section.SubTitle> */}
              {/* <Toggle> */}
              {/*   <Toggle.Text>Frit antal billetter</Toggle.Text> */}
              {/*   <Toggle.Button */}
              {/*     checked={ */}
              {/*       isType(form.tickets, "paid-single") && */}
              {/*       isType(form.tickets.quantity, "infinite") */}
              {/*     } */}
              {/*     onChange={(c) => { */}
              {/*       !c */}
              {/*         ? send({ */}
              {/*             type: "set.form.tickets", */}
              {/*             data: { */}
              {/*               type: "paid-single", */}
              {/*               quantity: { type: "limited", quantity: 1 }, */}
              {/*               price: isType(form.tickets, "paid-single") */}
              {/*                 ? form.tickets.price */}
              {/*                 : 0, */}
              {/*             }, */}
              {/*           }) */}
              {/*         : send({ */}
              {/*             type: "set.form.tickets", */}
              {/*             data: { */}
              {/*               type: "paid-single", */}
              {/*               quantity: { type: "infinite" }, */}
              {/*               price: isType(form.tickets, "paid-single") */}
              {/*                 ? form.tickets.price */}
              {/*                 : 0, */}
              {/*             }, */}
              {/*           }); */}
              {/*     }} */}
              {/*   /> */}
              {/* </Toggle> */}

              <AnimatePresence>
                {isType(form.tickets, "paid-single") &&
                  isType(form.tickets.quantity, "limited") && (
                    <motion.div {...dropInOutFromBottom}>
                      <TextInput
                        type="number"
                        className={hasErrorThenOutline("tickets")}
                        placeholder="100"
                        value={form.tickets.quantity.quantity}
                        onBlur={() => send({ type: "touch", data: "tickets" })}
                        onChange={(ev) => {
                          assert(isType(form.tickets, "paid-single"));
                          assert(isType(form.tickets.quantity, "limited"));
                          send({
                            type: "set.form.tickets",
                            data: {
                              ...form.tickets,
                              quantity: {
                                ...form.tickets.quantity,
                                quantity: Number(ev.currentTarget.value) ?? 0,
                              },
                            },
                          });
                        }}
                      />
                      <ErrorHint error={getError("tickets")} />
                    </motion.div>
                  )}
              </AnimatePresence>

              <Section.SubTitle>Tidsgrænse</Section.SubTitle>
              <Toggle>
                <Toggle.Text>Begræns billetsalg med en deadline</Toggle.Text>
                <Toggle.Button
                  checked={!!form.salesPeriod}
                  onChange={(c) => {
                    if (c) {
                      const now = new Date();
                      const to = isType(form.eventPeriod, "once")
                        ? form.eventPeriod.date
                        : now;
                      const from = now > to ? now : to;
                      send({
                        type: "set.form.salesPeriod",
                        data: new DatePeriod({
                          from: new Date(from),
                          to: new Date(to),
                        }),
                      });
                    } else {
                      send({ type: "set.form.salesPeriod", data: undefined });
                    }
                  }}
                />
              </Toggle>

              <AnimatePresence>
                {form.salesPeriod && (
                  <motion.div {...dropInOutFromBottom}>
                    <DatePeriodInput
                      className={hasErrorThenOutline("salesPeriod")}
                      onChange={(v) =>
                        send({
                          type: "set.form.salesPeriod",
                          data: DatePeriodSchema.parse(v),
                        })
                      }
                      onBlur={() =>
                        send({ type: "touch", data: "salesPeriod" })
                      }
                      period={form.salesPeriod}
                    />
                    <ErrorHint error={getError("salesPeriod")} />
                  </motion.div>
                )}
              </AnimatePresence>

              <AnimatePresence>
                {form.slug && (
                  <>
                    <EventUrlCopyThing title={form.title} slug={form.slug} />
                    <Button
                      variant="outlined"
                      onClick={() =>
                        copyToClipboard({
                          title: `${form.title}`,
                          url: `https://app.greeter.dk/events/${form.slug}`,
                          onError() {
                            toast("Kunne ikke kopiere linket", 1000);
                          },
                          onCopied(copyType) {
                            if (copyType === "copy") {
                              toast("📋 Event link kopieret", 1000);
                            }
                          },
                        })
                      }
                    >
                      <Button.IconCopy />
                      <Button.Text>Kopier link</Button.Text>
                    </Button>
                  </>
                )}
              </AnimatePresence>
            </Section>
            <Section>
              <Section.Title>Bordpakker</Section.Title>

              <Toggle>
                <Toggle.Text>
                  Ønsker du at vælge bordpakker til eventet?
                </Toggle.Text>
              </Toggle>

              <div className={css.Bundles}>
                {form.bundles
                  .map((id) => indexedBundles.get(id))
                  .filter(isTruthy)
                  .toSorted((a, b) => a.price - b.price)
                  .map((b) => (
                    <BundleCard
                      bundle={b}
                      onClick={() => setBundlePickerModalOpen(true)}
                    />
                  ))}
                {!form.bundles.length && (
                  <EmptyBundleCard
                    onClick={() => setBundlePickerModalOpen(true)}
                  />
                )}
              </div>
            </Section>
            <div style={{ padding: "1rem" }}>
              <button
                style={{
                  padding: "1rem",
                  width: "100%",
                  boxSizing: "border-box",
                  backgroundColor: "rgba(15, 20, 48, 1)",
                  border: "unset",
                  borderRadius: 10,
                  ...(formErrors
                    ? { opacity: 0.5, pointerEvents: "none" }
                    : {}),
                }}
                onClick={() => {
                  send({ type: "save" });
                }}
              >
                <span
                  style={{
                    fontSize: "1.5rem",
                    color: "rgba(252, 252, 253, 1)",
                  }}
                >
                  Gem event
                </span>
              </button>
            </div>
          </div>
          <Spinner
            reason={
              fetchTicketEventLoading
                ? "Henter"
                : saveTicketEventLoading && "Gemmer"
            }
          />
        </PageContainer>
      </IonContent>
      <IonModal
        isOpen={bundlePickerModalOpen}
        onWillDismiss={() => setBundlePickerModalOpen(false)}
        onIonModalWillPresent={() =>
          api.fetchBundlesRef.send({ type: "fetch", data: [] })
        }
      >
        <IonContent>
          <div
            style={{
              width: "100%",
              height: "100%",
              backgroundColor: "var(--gm-color-light-primary)",
              paddingTop: 16,
            }}
          >
            <PageContainer className={css.Bundles}>
              {bundles?.map((b) => (
                <BundleCard
                  bundle={b}
                  isSelectable
                  isSelected={indexedSelectedBundles.has(b.id)}
                  onClick={() =>
                    send({ type: "set.form.bundles.toggle", data: b.id })
                  }
                />
              ))}
            </PageContainer>
            <div
              style={{
                position: "absolute",
                bottom: 8,
                left: "50%",
                width: "100%",
                maxWidth: "600px",
                transform: "translateX(-50%)",
              }}
            >
              <Button
                variant="default"
                onClick={() => setBundlePickerModalOpen(false)}
              >
                <Button.Text>Gem valg</Button.Text>
              </Button>
            </div>
          </div>
        </IonContent>
      </IonModal>
      <IonModal
        isOpen={exitModalOpen}
        onWillDismiss={() => setExitModalOpen(false)}
        breakpoints={[0.3, 0.75, 1]}
      >
        <IonContent
          style={{ backgroundColor: "var(--gm-color-light-primary)" }}
        >
          <div
            style={{
              width: "100%",
              height: "100%",
              backgroundColor: "var(--gm-color-light-primary)",
            }}
          >
            <PageContainer
              style={{
                height: "100%",
                backgroundColor: "var(--gm-color-light-primary)",
              }}
            >
              <div
                style={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                }}
              >
                <EventUrlCopyThing title={form.title} slug={form.slug} />
                <Button
                  variant="outlined"
                  onClick={() => setExitModalOpen(false)}
                >
                  <Button.Text>Gå tilbage</Button.Text>
                </Button>
                <Button
                  onClick={async () => {
                    setExitModalOpen(false);
                    await copyToClipboard({
                      title: `${form.title}`,
                      url: `https://app.greeter.dk/events/${form.slug}`,
                      onError() {
                        toast("Kunne ikke kopiere linket", 1000);
                      },
                      onCopied(copyType) {
                        if (copyType === "copy") {
                          toast("📋 Event link kopieret", 1000);
                        }
                      },
                    });
                    history.push(Routes.mainGroup.ticketEvents.route());
                  }}
                >
                  <Button.IconCopy />
                  <Button.Text>Kopier og luk</Button.Text>
                </Button>
              </div>
            </PageContainer>
          </div>
        </IonContent>
      </IonModal>
    </IonPage>
  );
}
